import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import RecipientField from '../compose/RecipientField';
import { CALENDARS, addDays } from '../../lib/calendar';
import { isValidEmail } from '../../lib/compose';

const pad = (n) => String(n).padStart(2, '0');
const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeStr = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const parseLocal = (date, time) => new Date(`${date}T${time || '00:00'}:00`);

const selectClass = 'h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30';
const inputClass = selectClass;

const initialForm = (event, defaults) => {
  if (event) {
    const allDay = Boolean(event.all_day);
    const endShown = allDay ? addDays(event.end, -1) : event.end; // tüm gün etkinliklerde bitiş (hariç) ertesi gün gece yarısıdır
    return {
      title: event.title, color: event.color || 'blue', allDay,
      startDate: dateStr(event.start), startTime: timeStr(event.start),
      endDate: dateStr(endShown), endTime: timeStr(event.end),
      location: event.location || '', description: event.description || '',
      attendees: (event.attendees || []).map((email) => ({ name: email, email })), sendInvites: false,
    };
  }
  const start = defaults?.start || new Date();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    title: '', color: defaults?.color || 'blue', allDay: Boolean(defaults?.allDay),
    startDate: dateStr(start), startTime: timeStr(start), endDate: dateStr(end), endTime: timeStr(end),
    location: '', description: '', attendees: [], sendInvites: true,
  };
};

export const EventDialog = ({ open, onOpenChange, event, defaults, contacts, onSave }) => {
  const [form, setForm] = useState(initialForm(null, null));
  const [attendeeInput, setAttendeeInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(event);

  useEffect(() => {
    if (open) {
      setForm(initialForm(event, defaults));
      setAttendeeInput('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, defaults]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Başlangıç değişince süre korunur (bitiş birlikte kayar)
  const changeStart = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    const prevStart = parseLocal(f.startDate, f.allDay ? '00:00' : f.startTime);
    const prevEnd = parseLocal(f.endDate, f.allDay ? '00:00' : f.endTime);
    const nextStart = parseLocal(next.startDate, next.allDay ? '00:00' : next.startTime);
    if (!Number.isNaN(prevStart) && !Number.isNaN(prevEnd) && !Number.isNaN(nextStart)) {
      const shifted = new Date(nextStart.getTime() + (prevEnd.getTime() - prevStart.getTime()));
      next.endDate = dateStr(shifted);
      next.endTime = timeStr(shifted);
    }
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Başlık gerekli');
    let start; let end;
    if (form.allDay) {
      start = parseLocal(form.startDate, '00:00');
      end = addDays(parseLocal(form.endDate, '00:00'), 1);
    } else {
      start = parseLocal(form.startDate, form.startTime);
      end = parseLocal(form.endDate, form.endTime);
    }
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return setError('Tarih ve saat geçerli olmalı');
    if (end <= start) return setError('Bitiş, başlangıçtan sonra olmalı');

    const typed = attendeeInput.split(/[,;\s]+/).filter(isValidEmail);
    const emails = [...form.attendees.map((a) => a.email)];
    typed.forEach((t) => { if (!emails.some((x) => x.toLowerCase() === t.toLowerCase())) emails.push(t); });
    if (attendeeInput.split(/[,;\s]+/).filter(Boolean).some((t) => !isValidEmail(t))) return setError('Geçersiz katılımcı adresi var');

    setSaving(true);
    setError('');
    try {
      await onSave({
        title: form.title.trim(),
        color: form.color,
        all_day: form.allDay,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        location: form.location.trim() || null,
        description: form.description.trim() || null,
        attendees: emails,
        ...(isEdit ? {} : { send_invites: form.sendInvites && emails.length > 0 }),
      }, event);
      onOpenChange(false);
    } catch (err) {
      setError(err.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="event-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Etkinliği düzenle' : 'Yeni etkinlik'}</DialogTitle>
            <DialogDescription>Takvimde görünecek etkinlik bilgileri.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ev-title">Başlık</Label>
              <Input id="ev-title" value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={255} autoFocus data-testid="event-title" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ev-calendar">Takvim</Label>
                <select id="ev-calendar" className={selectClass} value={form.color} onChange={(e) => set({ color: e.target.value })} data-testid="event-calendar">
                  {CALENDARS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <Switch checked={form.allDay} onCheckedChange={(v) => set({ allDay: v })} data-testid="event-allday" />
                  Tüm gün
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ev-start-date">Başlangıç</Label>
                <input id="ev-start-date" type="date" className={inputClass} value={form.startDate} onChange={(e) => changeStart({ startDate: e.target.value })} data-testid="event-start-date" />
              </div>
              {!form.allDay && (
                <div className="space-y-2">
                  <Label htmlFor="ev-start-time">Saat</Label>
                  <input id="ev-start-time" type="time" className={inputClass} value={form.startTime} onChange={(e) => changeStart({ startTime: e.target.value })} data-testid="event-start-time" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="ev-end-date">Bitiş</Label>
                <input id="ev-end-date" type="date" className={inputClass} value={form.endDate} min={form.startDate} onChange={(e) => set({ endDate: e.target.value })} data-testid="event-end-date" />
              </div>
              {!form.allDay && (
                <div className="space-y-2">
                  <Label htmlFor="ev-end-time">Saat</Label>
                  <input id="ev-end-time" type="time" className={inputClass} value={form.endTime} onChange={(e) => set({ endTime: e.target.value })} data-testid="event-end-time" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ev-location">Konum</Label>
              <Input id="ev-location" value={form.location} onChange={(e) => set({ location: e.target.value })} maxLength={255} placeholder="Örn. Toplantı odası 2" data-testid="event-location" />
            </div>

            <div>
              <RecipientField
                label="Davet"
                testId="event-attendee"
                recipients={form.attendees}
                onChange={(list) => set({ attendees: list })}
                inputValue={attendeeInput}
                onInputChange={setAttendeeInput}
                contacts={contacts}
                placeholder="E-posta ekleyin"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ev-desc">Açıklama</Label>
              <textarea id="ev-desc" value={form.description} onChange={(e) => set({ description: e.target.value })} maxLength={5000} rows={3} className="w-full rounded-md border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30" data-testid="event-description" />
            </div>

            {!isEdit && (form.attendees.length > 0 || attendeeInput.trim()) && (
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Switch checked={form.sendInvites} onCheckedChange={(v) => set({ sendInvites: v })} data-testid="event-invites" />
                Katılımcılara davet e-postası gönder
              </label>
            )}

            {error && <p className="text-sm text-destructive" role="alert" data-testid="event-error">{error}</p>}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>İptal</Button>
            <Button type="submit" disabled={saving} data-testid="event-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Kaydet' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventDialog;
